import { useMemo } from 'react';
import type { AthleteTeamProfile } from '@strength/domain';
import { queries } from '@strength/zero-schema';
import { useZeroQuery } from '../../../../zero/useZeroQuery';
import { trpc } from '../../../../trpc/react';
import { getUrlTeam, useViewer } from '../../../context/viewer';
import { useIsZeroSurfaceActive } from '../../../zero/zero-active';

type UseAthletesQueryResult = {
  data: AthleteTeamProfile[] | undefined;
  status: 'loading' | 'error' | 'success' | 'pending';
  refetch: () => void;
};

/**
 * The tRPC route's server-side filters (`searchTerm`, `parentTrackId`).
 * Passed through to the tRPC path verbatim so the flag-off behavior stays
 * byte-identical to the pre-Zero call sites; the Zero path narrows the same
 * dimensions in its own query args at the call sites that need it.
 */
type UseAthletesQueryInput = {
  searchTerm?: string;
  parentTrackId?: string;
};

/**
 * Gate for the tRPC fallback path, for callers whose tRPC route is scoped by
 * the URL-derived `tw-team-ids` header: the Plan tree stays mounted for a
 * render after the URL drops its team UUID (e.g. navigating to /permissions),
 * and an enabled-then-headerless refetch 400s — see the invariant in
 * app/context/viewer.tsx. The Zero path needs no such gate (team scope comes
 * from the viewer's session, not the header).
 */
type UseAthletesQueryOptions = {
  enabled?: boolean;
  input?: UseAthletesQueryInput;
};

/** Zero's per-query details union → the tRPC query status vocabulary. */
function zeroDetailsStatus(
  details: { type: 'complete' | 'unknown' | 'cached' | 'error' } | undefined,
): UseAthletesQueryResult['status'] {
  if (details?.type === 'error') return 'error';
  if (details?.type === 'complete' || details?.type === 'cached')
    return 'success';
  return 'loading';
}

/** Maps a membership row (profile + attributes) to the tRPC route's shape. */
function toAthleteTeamProfile(row: {
  profile:
    | Omit<AthleteTeamProfile, 'tracks' | 'teams' | 'attributes'>
    | null
    | undefined;
  teamMembershipAttributes: AthleteTeamProfile['attributes'] | null | undefined;
}): AthleteTeamProfile | null {
  if (!row.profile) return null;
  return {
    ...row.profile,
    attributes: row.teamMembershipAttributes ?? null,
    tracks: [],
    teams: [],
  };
}

/**
 * The plan surface's athlete roster — the `roster.teamAthletes` read behind
 * the tracks surface flag (SC-5705). Zero when the surface is active, tRPC
 * otherwise (byte-identical behavior to before this hook existed). The roster
 * feeds the track form's athlete picker, the archived-track drawer, and the
 * plan/calendar athlete filters.
 *
 * Zero-path semantics, matched to the tRPC route:
 * - Rows are athlete-type ProfileTeamMembership memberships on the URL team
 *   with an active profile, one row per (profile, team) — the same scope the
 *   tRPC route gets from the `tw-team-ids` header (URL's first segment).
 * - The query sorts deterministically on root-table columns for cursor
 *   paging; the mapped result re-sorts by family name (then given name, id)
 *   so the rendered order matches the tRPC route's family-name order.
 * - The tRPC route's server-side `searchTerm` / `parentTrackId` filters
 *   stay server-side on the tRPC path (passed via `input`); the Zero path
 *   narrows the same dimensions in its own query args at the call sites
 *   that need them.
 * - The `tracks` aggregate stays empty: no plan-surface consumer reads it
 *   (the Teams area Profiles tab keeps its tRPC path). The `teams` aggregate
 *   — the manual transfer destinations — IS restored on the Zero path: a
 *   second tRPC read of the same route (Team is not replicated to Zero) whose
 *   result is merged into the mapped athletes when enableTeamLibrary is
 *   false.
 *
 * Zero data is live (IVM), so like useTracksQuery the refetch is a no-op
 * under Zero. The hook must be called unconditionally (Rules of Hooks) —
 * both read paths stay mounted and the flag only picks which result is
 * returned.
 */
export function useAthletesQuery(
  options: UseAthletesQueryOptions = {},
): UseAthletesQueryResult {
  const { enabled = true, input } = options;
  const isZeroActive = useIsZeroSurfaceActive('tracks');
  // The manual transfer flow this hook restores destination data for is only
  // reachable while the team library is disabled.
  const {
    org: {
      settings: { enableTeamLibrary },
    },
  } = useViewer();

  // The URL team — the same scope the `tw-team-ids` header gives the tRPC
  // route (see the invariant in app/context/viewer.tsx). The viewer's other
  // teams must NOT join the scope: the tRPC path never returns their
  // athletes here, and neither may the Zero path.
  const urlTeam = getUrlTeam(window.location.pathname);
  const teamIds = useMemo(() => (urlTeam ? [urlTeam] : []), [urlTeam]);

  // One gate for both reads: no URL team means no header would go out and
  // no team scope exists — disabled, and the hook reports TanStack's
  // disabled 'pending', not a loading state that never resolves.
  const queryEnabled = enabled && teamIds.length > 0;

  // Zero read — passed only when the surface is active (another surface's
  // flag can mount the provider while this surface stays on tRPC, and a live
  // query handed over then would subscribe and populate the cache anyway).
  const [memberships, details] = useZeroQuery(
    isZeroActive && queryEnabled
      ? queries.roster.teamAthletes({ teamIds })
      : undefined,
  );

  // tRPC read — the fallback path; disabled while Zero serves the surface.
  const trpcQuery = trpc.roster.teamAthletes.useQuery(input ?? {}, {
    staleTime: 10_000,
    enabled: queryEnabled && !isZeroActive,
  });

  // The Zero roster cannot carry the route's `teams` aggregate — the
  // athlete's org-wide athlete-type memberships, whose names live on Team,
  // a table never replicated to Zero. PlanContext derives the manual
  // transfer destinations from that aggregate when enableTeamLibrary is
  // false; with it empty the transfer control used to open with zero
  // destinations. Restore it over the same tRPC route — the transport and
  // scope this data already has — while the surface is on Zero and the
  // transfer flow is reachable. The input is always {}: the map must cover
  // every roster athlete, so no picker filter may narrow it.
  const destinationsQuery = trpc.roster.teamAthletes.useQuery(
    {},
    {
      staleTime: 10_000,
      enabled: queryEnabled && isZeroActive && !enableTeamLibrary,
    },
  );

  const athletes = useMemo(() => {
    if (isZeroActive) {
      // Restore the route's `teams` aggregate onto the Zero-mapped rows;
      // absent (still loading or failed) it stays empty and the transfer
      // option waits — see getMultiTeamManualTransferOptions.
      const teamsByProfileId = new Map(
        (destinationsQuery.data ?? []).map((a) => [a.id, a.teams]),
      );
      // The query's whereExists guarantees an active profile; the mapper's
      // null return narrows the generated optional relation type.
      return (memberships ?? [])
        .map(toAthleteTeamProfile)
        .filter((a) => a !== null)
        .map((a) => {
          const teams = teamsByProfileId.get(a.id);
          return teams ? { ...a, teams } : a;
        })
        .sort(
          (a, b) =>
            a.familyName.localeCompare(b.familyName) ||
            a.givenName.localeCompare(b.givenName) ||
            a.id.localeCompare(b.id),
        );
    }
    return trpcQuery.data ?? [];
  }, [isZeroActive, memberships, destinationsQuery.data, trpcQuery.data]);

  const status = !queryEnabled
    ? 'pending'
    : isZeroActive
      ? zeroDetailsStatus(details)
      : trpcQuery.status;

  // Zero data is live (IVM) — nothing to refetch; consumers' refetch calls
  // are no-ops while the surface is on Zero.
  const refetch = isZeroActive ? () => {} : () => void trpcQuery.refetch();

  return { data: athletes, status, refetch };
}
